import {
  api,
  CancelToken,
  getApiErrorMessage,
  normalizeListingsData,
} from "../../helpers";
import {
  SITE_SHOW_OPTIONS,
  SITE_SORT_OPTIONS,
  URL_API_LIVE,
} from "../../constants";
import { getMeta, metaInit } from "./Search.helpers";

import dayjs from "dayjs";
import qs from "query-string";

const minutesToExpire = 1;

/**
 * Action types
 * @property {string}
 */
export const types = {
  SEARCH_CLEAR: "SEARCH_CLEAR",
  SEARCH_CURRENT_META: "SEARCH_CURRENT_META",
  SEARCH_ERROR: "SEARCH_ERROR",
  SEARCH_LOADING: "SEARCH_LOADING",
  SEARCH_SUCCESS: "SEARCH_SUCCESS",
};

/**
 * @namespace Reducers
 */
export const reducers = {
  /**
   * Updates current search meter type state
   * @param {string} state
   * @param {object} action
   * @param {string} action.type
   * @param {object} action.meta
   * @memberof Reducers
   */
  searchCurrentMeta(state = metaInit, action) {
    switch (action.type) {
      case types.SEARCH_CLEAR:
        return metaInit;
      case types.SEARCH_CURRENT_META:
        return action.meta;
      default:
        return state;
    }
  },

  /**
   * Updates search error state
   * @param {string} state
   * @param {object} action
   * @param {string} action.type
   * @param {string} action.error
   * @memberof Reducers
   */
  searchError: (state = null, action) => {
    switch (action.type) {
      case types.SEARCH_ERROR:
        return action.error;
      case types.SEARCH_LOADING:
      case types.SEARCH_SUCCESS:
        return null;
      default:
        return state;
    }
  },

  /**
   * Updates search loading state
   * @param {boolean} state
   * @param {object} action
   * @param {string} action.type
   * @memberof Reducers
   */
  searchLoading: (state = false, action) => {
    switch (action.type) {
      case types.SEARCH_ERROR:
      case types.SEARCH_SUCCESS:
        return false;
      case types.SEARCH_LOADING:
        return true;
      default:
        return state;
    }
  },

  /**
   * Updates search results state
   * @param {array} state
   * @param {object} action
   * @param {string} action.type
   * @param {array} action.results
   * @memberof Reducers
   */
  searchResults: (state = {}, action) => {
    switch (action.type) {
      case types.SEARCH_CLEAR:
        return {};
      case types.SEARCH_SUCCESS:
        return action.results;
      case types.SEARCH_ERROR:
      default:
        return state;
    }
  },

  /**
   * Updates search updated state
   * @param {string} state
   * @param {object} action
   * @param {string} action.type
   * @param {string} action.updated
   * @memberof Reducers
   */
  searchUpdated: (state = null, action) => {
    switch (action.type) {
      case types.SEARCH_CLEAR:
        return null;
      case types.SEARCH_SUCCESS:
        return action.updated;
      default:
        return state;
    }
  },
};

/**
 * @namespace Actions
 */
export const actions = {
  /**
   * Cancel search API request
   * * Updated with a new Axios cancel token after an API request is initiated
   * @memberof Actions
   */
  cancel: () => false,

  /**
   * Clear cached search results
   * @memberof Actions
   */
  clear: () => ({
    type: types.SEARCH_CLEAR,
  }),

  /**
   * Fetch search results from API
   * @param {string} query
   * @memberof Actions
   */
  fetch: query => (dispatch, getState) => {
    const { searchLoading } = getState();

    if (searchLoading) {
      actions.cancel("Canceled");
    }

    dispatch(actions.handleLoading());

    const queryParsed = qs.parse(query);

    if (!queryParsed.page) {
      queryParsed.page = 1;
    }

    if (
      !queryParsed.limit &&
      !!SITE_SHOW_OPTIONS &&
      !!SITE_SHOW_OPTIONS[0] &&
      !!SITE_SHOW_OPTIONS[0].value
    ) {
      queryParsed.limit = SITE_SHOW_OPTIONS[0].value;
    }

    if (
      !queryParsed.sort &&
      !!SITE_SORT_OPTIONS &&
      !!SITE_SORT_OPTIONS[0] &&
      !!SITE_SORT_OPTIONS[0].value
    ) {
      queryParsed.sort = SITE_SORT_OPTIONS[0].value;
    }

    queryParsed.newLimit = 18;

    const nextQuery = `?${qs.stringify(queryParsed)}`;

    api
      .get(`${URL_API_LIVE}${nextQuery}`, {
        cancelToken: new CancelToken(c => {
          actions.cancel = c;
        }),
      })
      .then(response => {
        if (!!response && !!response.data && !!response.data.data) {
          dispatch(actions.parse(response.data.data, query));
        } else {
          dispatch(actions.handleError(getApiErrorMessage(response)));
        }
      })
      .catch(error => {
        if (!!error.message && error.message === "Canceled") {
          return false;
        }

        dispatch(actions.handleError(getApiErrorMessage(error)));
      });
  },

  /**
   * Handle search error messages
   * @param {string} message
   * @memberof Actions
   */
  handleError: message => ({
    type: types.SEARCH_ERROR,
    error: message,
  }),

  /**
   * Handle search loadng events
   * @memberof Actions
   */
  handleLoading: () => ({
    type: types.SEARCH_LOADING,
  }),

  /**
   * Handle current search metadata
   * @param {object} meta
   * @memberof Actions
   */
  handleSearchCurrentMeta: meta => ({
    type: types.SEARCH_CURRENT_META,
    meta: meta,
  }),

  /**
   * Handle search success
   * @param {array} results
   * @memberof Actions
   */
  handleSuccess: results => ({
    type: types.SEARCH_SUCCESS,
    results: results,
    updated: dayjs().format(),
  }),

  /**
   * Parse listing API response for additional
   * information needed in the UI
   * @param {*} data
   * @param {string} query
   * @param {number} page
   * @memberof Actions
   */
  parse: (data, query) => (dispatch, getState) => {
    const { filters, listings, newListings } = data;
    const { login } = getState();
    const authenticated = !!login.auth.expires;
    const expires = dayjs().add(minutesToExpire, "minutes").format();

    // Search queries that return no results default to
    // `meterType = "hours"` which flips the Trucks meterType
    // from "miles" and causes unneccessary rerenders
    if (
      filters?.sourceDivision === "truck" ||
      filters?.sourceDivision === "kyrish"
    ) {
      filters.meterType = "miles";
    }

    const searchResults = {
      authenticated,
      expires,
      meta: getMeta(filters, listings),
      newListings: normalizeListingsData(newListings),
      query,
      results: normalizeListingsData(listings.data),
    };

    dispatch(actions.handleSuccess(searchResults));
  },

  /**
   * Reset current search metadata
   * @memberof Actions
   */
  resetSearchCurrentMeta: () => ({
    type: types.SEARCH_CURRENT_META,
    meta: metaInit,
  }),
};
